import { textField } from '@lib/form';
import PropTypes from 'prop-types';
// Deliberately not the `TextInput` atom: it hardcodes a multiline `paddingTop` through
// `slotProps`, which outranks the form theme's padding and cannot be overridden from here.
// That atom is shared with ModelRequestForm, AddressForm and the workshop tool, so the
// form's own field goes straight to MUI instead of changing it for all of them.
import { InputAdornment, TextField as MuiTextField } from '@mui/material';
import FieldLabel from '../FieldLabel';
import getFieldStatus from '../fieldStatus';

/**
 * The CMS input type, as the DOM wants it.
 *
 * `number` deliberately does not map to `type="number"`: that control rounds its own value
 * to the browser locale, swallows the German decimal comma, and scrolls the value away
 * under a stray mouse wheel. `inputMode` brings up the same keypad on a phone without any
 * of that, and `textField.validationSchema` does the checking.
 */
const INPUT_TYPES = {
  text: { type: 'text', inputMode: undefined },
  number: { type: 'text', inputMode: 'decimal' },
  email: { type: 'email', inputMode: 'email' },
  tel: { type: 'tel', inputMode: 'tel' },
};

export default function TextField(props) {
  const {
    name,
    disabled,
    value = '',
    error,
    touched,
    label = '',
    isRequired,
    helperText = '',
    isMultiline,
    inputType = 'text',
    startAdornment = '',
    endAdornment = '',
    handleChange,
    handleBlur,
    readOnly,
  } = props;
  const { hasError, message } = getFieldStatus({ error, touched, helperText });
  // A textarea carries no input type, so multiline wins — the same precedence the
  // validation schema applies.
  const { type, inputMode } = (!isMultiline && INPUT_TYPES[inputType]) || INPUT_TYPES.text;

  return (
    <MuiTextField
      disabled={disabled}
      error={hasError}
      fullWidth
      helperText={message}
      label={<FieldLabel isRequired={isRequired} label={label} />}
      minRows={isMultiline ? 3 : null}
      multiline={isMultiline}
      name={name}
      onBlur={handleBlur}
      onChange={(event) => handleChange(event.target.value)}
      slotProps={{
        input: {
          startAdornment: startAdornment
            && <InputAdornment position="start">{startAdornment}</InputAdornment>,
          endAdornment: endAdornment
            && <InputAdornment position="end">{endAdornment}</InputAdornment>,
          readOnly,
        },
        // `aria-required` rather than MUI's `required`, which would print the asterisk the
        // redesign removed. No `aria-label` here: the `<label>` is already the accessible
        // name, and overriding it would strip the "· optional" marker back out of it.
        htmlInput: { 'aria-required': isRequired || undefined, inputMode },
      }}
      type={type}
      value={value}
    />
  );
}

TextField.propTypes = {
  name: PropTypes.string.isRequired,
  disabled: PropTypes.bool,
  value: PropTypes.string,
  error: PropTypes.string,
  touched: PropTypes.bool,
  label: PropTypes.string,
  isRequired: PropTypes.bool,
  helperText: PropTypes.string,
  isMultiline: PropTypes.bool,
  inputType: PropTypes.oneOf(Object.keys(INPUT_TYPES)),
  startAdornment: PropTypes.string,
  endAdornment: PropTypes.string,
  handleChange: PropTypes.func.isRequired,
  handleBlur: PropTypes.func.isRequired,
  readOnly: PropTypes.bool,
};

TextField.getDefaultValue = textField.getDefaultValue;
TextField.serializeValue = textField.serializeValue;
TextField.deserializeValue = textField.deserializeValue;
TextField.getDisplayValue = textField.getDisplayValue;
TextField.validationSchema = textField.validationSchema;
TextField.graphQlSchema = `
typeText {
  id
  isMultiline
  inputType
  minValue
  maxValue
  startAdornment
  endAdornment
  preFilledValue
}
`;
